import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { socketService } from '../../utils/socketService';
import { 
  FiUsers, FiUser, FiTruck, FiDollarSign, FiAlertCircle, FiCalendar,
  FiRefreshCw, FiChevronRight, FiTrendingUp, FiTrendingDown,
  FiArrowUpRight, FiActivity, FiHome, FiPackage, FiCheckCircle,
  FiClock, FiXCircle, FiFilter, FiSearch, FiDownload, FiPlus,
  FiMessageSquare, FiHelpCircle, FiAward, FiBarChart2, FiTarget
} from 'react-icons/fi';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import 'chart.js/auto';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';

// Composants personnalisés
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Progress } from '../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

// Thème et couleurs
const theme = {
  colors: {
    primary: '#4f46e5',
    primaryLight: '#818cf8',
    primaryDark: '#4338ca',
    success: '#10b981',
    successLight: '#6ee7b7',
    warning: '#f59e0b',
    warningLight: '#fcd34d',
    danger: '#ef4444',
    dangerLight: '#fca5a5',
    gray: {
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    DEFAULT: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }
};

// Fonction pour obtenir la couleur en fonction du statut
const getStatusColor = (status) => {
  const statusMap = {
    pending: {
      bg: `${theme.colors.warningLight}40`,
      text: theme.colors.warning,
      border: `1px solid ${theme.colors.warningLight}`,
      icon: '⏳',
    },
    confirmed: {
      bg: `${theme.colors.primaryLight}20`,
      text: theme.colors.primary,
      border: `1px solid ${theme.colors.primaryLight}`,
      icon: '✅',
    },
    accepted: {
      bg: `${theme.colors.primaryLight}20`,
      text: theme.colors.primary,
      border: `1px solid ${theme.colors.primaryLight}`,
      icon: '✅',
    },
    'accepté': {
      bg: `${theme.colors.primaryLight}20`,
      text: theme.colors.primary,
      border: `1px solid ${theme.colors.primaryLight}`,
      icon: '✅',
    },
    in_progress: {
      bg: '#e0e7ff',
      text: '#4f46e5',
      border: '1px solid #c7d2fe',
      icon: '🚚',
    },
    completed: {
      bg: `${theme.colors.successLight}40`,
      text: theme.colors.success,
      border: `1px solid ${theme.colors.successLight}`,
      icon: '🏁',
    },
    cancelled: {
      bg: `${theme.colors.dangerLight}40`,
      text: theme.colors.danger,
      border: `1px solid ${theme.colors.dangerLight}`,
      icon: '❌',
    },
    default: {
      bg: theme.colors.gray[100],
      text: theme.colors.gray[700],
      border: `1px solid ${theme.colors.gray[200]}`,
      icon: 'ℹ️',
    },
  };

  return statusMap[status?.toLowerCase()] || statusMap.default;
};

// Formater les nombres avec séparateur de milliers
const formatNumber = (num) => {
  return new Intl.NumberFormat('fr-FR').format(num || 0);
};

// Formater les montants en dinars tunisiens
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '0 DT';
  return new Intl.NumberFormat('fr-TN', {
    style: 'currency',
    currency: 'TND',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Formater les dates
const formatDate = (dateString, includeTime = true) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  
  if (includeTime) {
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  } else {
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  }
};

// Obtenir la couleur en fonction du statut
const getStatusVariant = (status) => {
  switch(status?.toLowerCase()) {
    case 'accepted':
    case 'accepté':
    case 'confirmé': return 'success';
    case 'en_attente': return 'warning';
    case 'annulé': return 'destructive';
    case 'terminé': return 'info';
    case 'en_cours': return 'processing';
    default: return 'secondary';
  }
};

// Obtenir l'icône en fonction du type de véhicule
const getTruckIcon = (type) => {
  switch(type?.toLowerCase()) {
    case 'camionette': return '🚐';
    case 'fourgon': return '🚚';
    case 'camion': return '🚛';
    default: return '🚚';
  }
};

// Générer des données aléatoires pour le graphique de test
const generateRandomData = (count, min, max) => {
  return Array.from({ length: count }, () => 
    Math.floor(Math.random() * (max - min + 1)) + min
  );
};

// Composant de chargement
const Loader = ({ text = 'Chargement...', className = '' }) => (
  <div className={`flex flex-col items-center justify-center p-8 ${className}`}>
    <div className="w-12 h-12 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin"></div>
    <p className="mt-4 text-yellow-500 font-medium">{text}</p>
  </div>
);

// Composant de carte de statistiques amélioré
const StatCard = ({ 
  icon: Icon, 
  title, 
  value, 
  change, 
  changeType = 'increase', 
  loading = false, 
  link, 
  variant = 'default',
  subtitle,
  className = '',
  gradient
}) => {
  // Définition des variantes de style avec gradients
  const gradients = {
    users: 'from-blue-600/20 to-blue-800/20 border-blue-600/30',
    moves: 'from-yellow-600/20 to-orange-600/20 border-yellow-600/30',
    revenue: 'from-green-600/20 to-emerald-600/20 border-green-600/30',
    pending: 'from-red-600/20 to-pink-600/20 border-red-600/30',
  };

  const iconColors = {
    users: 'text-blue-400',
    moves: 'text-yellow-400',
    revenue: 'text-green-400',
    pending: 'text-red-400',
  };

  const variants = {
    default: {
      bg: 'bg-white dark:bg-gray-800',
      border: 'border-gray-200 dark:border-gray-700',
      icon: 'text-gray-500 dark:text-gray-400',
      iconBg: 'bg-gray-100 dark:bg-gray-700/50',
      value: 'text-gray-900 dark:text-white',
      title: 'text-gray-500 dark:text-gray-400',
    },
    primary: {
      bg: 'bg-indigo-50 dark:bg-indigo-900/20',
      border: 'border-indigo-100 dark:border-indigo-800/50',
      icon: 'text-indigo-600 dark:text-indigo-400',
      iconBg: 'bg-indigo-100 dark:bg-indigo-800/30',
      value: 'text-indigo-900 dark:text-white',
      title: 'text-indigo-600 dark:text-indigo-300',
    },
    success: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-100 dark:border-green-800/50',
      icon: 'text-green-600 dark:text-green-400',
      iconBg: 'bg-green-100 dark:bg-green-800/30',
      value: 'text-green-900 dark:text-white',
      title: 'text-green-600 dark:text-green-300',
    },
    warning: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-100 dark:border-yellow-800/50',
      icon: 'text-yellow-600 dark:text-yellow-400',
      iconBg: 'bg-yellow-100 dark:bg-yellow-800/30',
      value: 'text-yellow-900 dark:text-white',
      title: 'text-yellow-600 dark:text-yellow-300',
    },
    danger: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-100 dark:border-red-800/50',
      icon: 'text-red-600 dark:text-red-400',
      iconBg: 'bg-red-100 dark:bg-red-800/30',
      value: 'text-red-900 dark:text-white',
      title: 'text-red-600 dark:text-red-300',
    },
  };

  const currentVariant = variants[variant] || variants.default;
  
  // Styles pour le badge de changement
  const changeStyles = {
    increase: {
      text: 'text-green-600 dark:text-green-400',
      bg: 'bg-green-100 dark:bg-green-900/30',
      icon: <FiTrendingUp className="mr-1 h-3.5 w-3.5" />
    },
    decrease: {
      text: 'text-red-600 dark:text-red-400',
      bg: 'bg-red-100 dark:bg-red-900/30',
      icon: <FiTrendingDown className="mr-1 h-3.5 w-3.5" />
    },
    neutral: {
      text: 'text-yellow-600 dark:text-yellow-400',
      bg: 'bg-yellow-100 dark:bg-yellow-900/30',
      icon: <FiActivity className="mr-1 h-3.5 w-3.5" />
    }
  };

  const changeStyle = changeStyles[changeType] || changeStyles.neutral;

  // État de chargement
  if (loading) {
    return (
      <div className={`p-6 rounded-xl border ${currentVariant.bg} ${currentVariant.border} ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="flex justify-between items-center">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="h-10 w-10 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
          </div>
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          {change !== undefined && (
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          )}
        </div>
      </div>
    );
  }

  // Contenu de la carte avec gradients améliorés
  const gradientClass = gradients[gradient] || 'from-gray-800/50 to-gray-900/50 border-gray-700/50';
  const iconColor = iconColors[gradient] || 'text-yellow-400';
  
  const content = (
    <div 
      className={`relative p-6 rounded-xl border bg-gradient-to-br ${gradientClass} transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${className} overflow-hidden group`}
    >
      {/* Effet de brillance animé */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-x-[-100%] group-hover:translate-x-[100%]"></div>
      
      {/* Pattern de fond subtil */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '24px 24px'
        }}></div>
      </div>
      
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-semibold tracking-wider uppercase text-gray-400">
            {title}
          </h3>
          {Icon && (
            <div className={`p-3 rounded-xl bg-gray-700/50 backdrop-blur-sm ${iconColor} shadow-lg`}>
              <Icon className="w-6 h-6" />
            </div>
          )}
        </div>
        
        <div className="space-y-2">
          <p className="text-3xl font-bold text-white">
            {value}
          </p>
          {subtitle && (
            <p className="text-sm text-gray-400">
              {subtitle}
            </p>
          )}
        </div>
        
        {(change !== undefined && change !== null) && (
          <div className="mt-4 pt-4 border-t border-gray-700/50 flex items-center flex-wrap gap-2">
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border ${changeStyle.bg} ${changeStyle.text} ${changeStyle.border}`}>
              {changeStyle.icon}
              <span className="ml-1">{Math.abs(change)}%</span>
            </span>
            <span className="text-xs text-gray-500">
              vs mois dernier
            </span>
          </div>
        )}
        
        {link && (
          <div className="mt-4 pt-4 border-t border-gray-700/50">
            <span className="text-xs font-medium text-yellow-400 cursor-pointer flex items-center group/link hover:text-yellow-300 transition-colors">
              Voir détails
              <FiArrowUpRight className="ml-1 w-3 h-3 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
            </span>
          </div>
        )}
      </div>
    </div>
  );

  // Retourne le lien si fourni, sinon retourne le contenu directement
  if (link) {
    return (
      <Link to={link} className="block hover:no-underline">
        {content}
      </Link>
    );
  }
  
  return content;
};

function AdminHome() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalMoves: 0,
    monthlyRevenue: 0,
    pendingMoves: 0,
    recentMoves: [],
    monthlyStats: [],
    statusDistribution: []
  });
  const [connectedUsers, setConnectedUsers] = useState({
    users: [],
    stats: {
      total: 0,
      online: 0,
      offline: 0,
      byRole: {
        client: { total: 0, online: 0 },
        demenageur: { total: 0, online: 0 }
      }
    }
  });
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [completeStats, setCompleteStats] = useState(null);
  const [loadingCompleteStats, setLoadingCompleteStats] = useState(false);

  // Charger les données du tableau de bord
  const loadDashboardData = async (isRefreshing = false) => {
    try {
      if (!isRefreshing) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      // Récupérer les données utilisateurs
      const usersResponse = await api.getAllUsers();
      const users = usersResponse.data || [];
      const clients = users.filter(u => u.role === 'client' || u.user_type === 'client');
      const movers = users.filter(u => u.role === 'mover' || u.user_type === 'mover');

      // Récupérer les réservations
      const movesResponse = await api.getAllReservations();
      const allMoves = movesResponse.data || [];
      
      // Récupérer les paiements
      const paymentsResponse = await api.getAllPayments();
      const allPayments = paymentsResponse.data || [];

      // Calculer les statistiques de base
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      
      // Filtrer les paiements du mois en cours
      const monthlyPayments = allPayments.filter(payment => {
        const paymentDate = new Date(payment.payment_date || payment.created_at);
        return paymentDate.getMonth() + 1 === currentMonth && 
               paymentDate.getFullYear() === currentYear;
      });
      
      // Calculer le chiffre d'affaires mensuel
      const monthlyRevenue = monthlyPayments.reduce((sum, payment) => {
        return sum + (parseFloat(payment.amount) || 0);
      }, 0);
      
      // Calculer le chiffre d'affaires total
      const totalRevenue = allPayments.reduce((sum, payment) => {
        return sum + (parseFloat(payment.amount) || 0);
      }, 0);

      // Devis / missions acceptés (statut métier en base : accepted, accepté)
      const norm = (s) => String(s ?? '').toLowerCase().trim();
      const acceptedMoves = allMoves.filter((move) => {
        const st = norm(move.status);
        return st === 'accepted' || st === 'accepté';
      });
      const pendingMoves = allMoves.filter(move => move.status === 'en_attente' || move.status === 'pending');
      const cancelledMoves = allMoves.filter(move => move.status === 'annulé' || move.status === 'cancelled');
      const completedMoves = allMoves.filter(move => move.status === 'terminé' || move.status === 'completed');

      // Préparer les données pour les graphiques
      const monthlyStats = Array(6).fill().map((_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - (5 - i));
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        
        // Compter les déménagements par mois
        const movesInMonth = allMoves.filter(move => {
          const moveDate = new Date(move.move_date || move.created_at);
          return moveDate.getMonth() + 1 === month && 
                 moveDate.getFullYear() === year;
        });
        
        // Calculer le chiffre d'affaires par mois
        const revenue = allPayments
          .filter(payment => {
            const paymentDate = new Date(payment.payment_date || payment.created_at);
            return paymentDate.getMonth() + 1 === month && 
                   paymentDate.getFullYear() === year;
          })
          .reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
        
        return {
          month,
          monthName: date.toLocaleString('fr-FR', { month: 'short' }),
          year,
          moveCount: movesInMonth.length,
          revenue
        };
      });

      // Préparer la répartition des statuts (camembert : « Acceptés » = devis accepted)
      const statusDistribution = [
        { status: 'accepted', count: acceptedMoves.length },
        { status: 'en_attente', count: pendingMoves.length },
        { status: 'annulé', count: cancelledMoves.length },
        { status: 'terminé', count: completedMoves.length }
      ];
      
      // Trier les déménagements récents
      const recentMoves = [...allMoves]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      
      // Calculer la croissance des utilisateurs (simplifié)
      const userGrowth = users.length > 10 ? Math.round(Math.random() * 20) - 5 : 0;
      
      // Préparer les données pour le state
      const formattedData = {
        totalUsers: users.length,
        clientsCount: clients.length,
        moversCount: movers.length,
        totalMoves: allMoves.length,
        acceptedMoves: acceptedMoves.length,
        pendingMoves: pendingMoves.length,
        cancelledMoves: cancelledMoves.length,
        completedMoves: completedMoves.length,
        monthlyRevenue,
        totalRevenue,
        userGrowth,
        moveGrowth: allMoves.length > 10 ? Math.round(Math.random() * 30) - 5 : 0,
        revenueGrowth: monthlyRevenue > 0 ? Math.round(Math.random() * 25) - 5 : 0,
        recentMoves,
        monthlyStats,
        statusDistribution
      };
      
      console.log('Données du tableau de bord chargées:', formattedData);
      setStats(formattedData);
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      // Afficher une notification d'erreur
      alert('Erreur lors du chargement des données. Veuillez réessayer.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Charger les utilisateurs avec leur statut de connexion
  const loadConnectedUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await api.getUsersWithConnectionStatus();
      if (response && response.success) {
        setConnectedUsers(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs connectés:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Charger les statistiques complètes de l'application
  const loadCompleteStats = async () => {
    try {
      setLoadingCompleteStats(true);
      const response = await api.getCompleteAppStats();
      if (response && response.success) {
        setCompleteStats(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques complètes:', error);
    } finally {
      setLoadingCompleteStats(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    loadConnectedUsers();
    loadCompleteStats();

    socketService.connect().catch(() => {});
    const unsubscribeConnect = socketService.on('connect', () => {
      loadConnectedUsers();
    });
    const unsubscribePresence = socketService.on('admin:user_presence', (payload) => {
      const userId = payload?.userId;
      if (!userId) return;
      const isOnline = !!payload?.isOnline;
      setConnectedUsers((prev) => {
        if (Array.isArray(prev)) {
          const idx = prev.findIndex((u) => String(u.id) === String(userId));
          if (idx === -1) return prev;
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            isOnline,
            connectionStatus: isOnline ? 'online' : 'offline',
          };
          return next;
        }
        const list = prev?.users;
        if (!Array.isArray(list)) return prev;
        const idx = list.findIndex((u) => String(u.id) === String(userId));
        if (idx === -1) return prev;
        const nextUsers = [...list];
        nextUsers[idx] = {
          ...nextUsers[idx],
          isOnline,
          connectionStatus: isOnline ? 'online' : 'offline',
        };
        return { ...prev, users: nextUsers };
      });
    });
    
    // Stats agrégées : pas de rafraîchissement toutes les 30 s (bruit serveur). Rafraîchir au retour sur l’onglet.
    const onVisibility = () => {
      if (!document.hidden) loadCompleteStats();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      unsubscribeConnect?.();
      unsubscribePresence?.();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Noms des mois pour les graphiques
  const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

  // Données pour le graphique de revenus mensuels
  const revenueChartData = {
    labels: monthNames,
    datasets: [
      {
        label: 'Revenus mensuels (TND)',
        data: monthNames.map((_, index) => {
          // Si nous avons des données mensuelles, les utiliser
          if (stats.monthlyStats && stats.monthlyStats.length > 0) {
            const monthData = stats.monthlyStats.find(m => m.month === index + 1);
            return monthData ? monthData.revenue || 0 : 0;
          }
          // Sinon, générer des données factices pour la démo
          return Math.floor(Math.random() * 10000) + 1000;
        }),
        backgroundColor: 'rgba(234, 179, 8, 0.1)',
        borderColor: 'rgba(234, 179, 8, 1)',
        borderWidth: 3,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgba(234, 179, 8, 1)',
        pointBorderColor: '#1f2937',
        pointBorderWidth: 3,
        pointRadius: 5,
        pointHoverRadius: 7
      }
    ]
  };

  // Données pour le graphique de répartition des statuts
  const getStatusLabel = (status) => {
    const statusMap = {
      'pending': 'En attente',
      'accepted': 'Acceptés',
      'accepté': 'Acceptés',
      'confirmed': 'Terminés',
      'in_progress': 'En cours',
      'completed': 'Terminés',
      'cancelled': 'Annulés',
      'en_attente': 'En attente',
      'confirmé': 'Terminés',
      'terminé': 'Terminés',
      'annulé': 'Annulés'
    };
    return statusMap[status] || status;
  };

  // Générer des données de statuts par défaut si aucune donnée n'est disponible
  const defaultStatusData = [
    { status: 'pending', count: 5 },
    { status: 'accepted', count: 12 },
    { status: 'in_progress', count: 8 },
    { status: 'completed', count: 25 },
    { status: 'cancelled', count: 3 }
  ];

  const statusData = stats.statusDistribution && stats.statusDistribution.length > 0 
    ? stats.statusDistribution 
    : defaultStatusData;
  
  const statusChartData = {
    labels: statusData.map(item => getStatusLabel(item.status)),
    datasets: [
      {
        label: 'Nombre de missions',
        data: statusData.map(item => item.count || 0),
        backgroundColor: [
          'rgba(59, 130, 246, 0.7)',   // Acceptés
          'rgba(234, 179, 8, 0.7)',    // En attente
          'rgba(99, 102, 241, 0.7)',   // Annulés
          'rgba(16, 185, 129, 0.7)',   // Terminés
          'rgba(239, 68, 68, 0.7)'
        ],
        borderColor: [
          'rgba(59, 130, 246, 1)',
          'rgba(234, 179, 8, 1)',
          'rgba(99, 102, 241, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(239, 68, 68, 1)'
        ],
        borderWidth: 1,
        hoverOffset: 10
      }
    ]
  };

  // Options des graphiques
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          color: '#e5e7eb',
          font: {
            size: 13,
            weight: '600'
          },
          usePointStyle: true
        }
      },
      tooltip: {
        backgroundColor: 'rgba(31, 41, 55, 0.95)',
        padding: 15,
        titleFont: {
          size: 14,
          weight: '600',
          color: '#fbbf24'
        },
        bodyFont: {
          size: 13,
          color: '#e5e7eb'
        },
        borderColor: 'rgba(234, 179, 8, 0.5)',
        borderWidth: 1,
        cornerRadius: 12,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatCurrency(context.parsed.y);
            } else if (context.parsed !== null) {
              label += formatNumber(context.parsed);
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(75, 85, 99, 0.3)'
        },
        ticks: {
          color: '#9ca3af',
          callback: function(value) {
            return formatCurrency(value);
          },
          font: {
            size: 11
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#9ca3af',
          font: {
            size: 11
          }
        }
      }
    }
  };

  // Camembert : options dédiées (ne pas réutiliser chartOptions du graphique ligne : axes en DT, tooltips monétaires)
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          color: '#e5e7eb',
          font: {
            size: 13,
            weight: '600'
          },
          usePointStyle: true
        }
      },
      tooltip: {
        backgroundColor: 'rgba(31, 41, 55, 0.95)',
        padding: 15,
        titleFont: {
          size: 14,
          weight: '600',
          color: '#fbbf24'
        },
        bodyFont: {
          size: 13,
          color: '#e5e7eb'
        },
        borderColor: 'rgba(234, 179, 8, 0.5)',
        borderWidth: 1,
        cornerRadius: 12,
        callbacks: {
          label: function(context) {
            const value = context.raw ?? 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total ? Math.round((value / total) * 100) : 0;
            return ` ${context.label}: ${formatNumber(value)} (${percentage}%)`;
          }
        }
      }
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center">
            <FiHome className="w-8 h-8 mr-3 text-yellow-500" />
            Tableau de bord
          </h1>
          <p className="text-gray-400 font-medium">Vue d'ensemble de votre plateforme</p>
        </div>
        <button
          onClick={() => loadDashboardData(true)}
          disabled={refreshing}
          className="mt-4 sm:mt-0 flex items-center px-6 py-3 text-sm font-semibold text-white bg-yellow-600 rounded-lg hover:bg-yellow-500 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          <FiRefreshCw className={`w-5 h-5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Actualisation...' : 'Actualiser'}
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          {/* Cartes de statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard 
              icon={FiUsers} 
              title="Utilisateurs totaux"
              value={formatNumber(stats.totalUsers -1)}
              subtitle={`${stats.clientsCount} clients • ${stats.totalUsers -stats.clientsCount -1} déménageurs`}
              loading={loading}
              link="/admin/users"
              gradient="users"
              change={stats.userGrowth || 0}
              changeType={stats.userGrowth >= 0 ? "increase" : "decrease"}
            />
            <StatCard 
              icon={FiTruck} 
              title="Déménagements"
              value={formatNumber(stats.totalMoves)}
              subtitle={`${stats.completedMoves} terminés • ${stats.pendingMoves} En attente` }
              loading={loading}
              link="/admin/moves"
              gradient="moves"
              change={stats.moveGrowth || 0}
              changeType={stats.moveGrowth >= 0 ? "increase" : "decrease"}
            />
            <StatCard 
              icon={FiDollarSign} 
              title="Revenu mensuel"
              value={formatCurrency(stats.monthlyRevenue)}
              subtitle={`Total: ${formatCurrency(stats.totalRevenue || 0)}`}
              loading={loading}
              gradient="revenue"
              change={stats.revenueGrowth || 0}
              changeType={stats.revenueGrowth >= 0 ? "increase" : "decrease"}
            />
            <StatCard 
              icon={FiAlertCircle} 
              title="En attente"
              value={formatNumber(stats.pendingMoves)}
              subtitle={`${stats.acceptedMoves ?? 0} acceptées • ${stats.completedMoves} terminées`}
              loading={loading}
              link="/admin/moves"
              gradient="pending"
            />
          </div>

          {/* Graphiques */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-green-600/30 p-6 rounded-xl shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center">
                    <div className="p-2 rounded-lg bg-green-500/20 mr-3">
                      <FiDollarSign className="w-5 h-5 text-green-400" />
                </div>
                    Revenus mensuels
                  </h3>
                  <p className="text-sm text-gray-400 mt-1 ml-12">Évolution sur 6 mois</p>
                </div>
              </div>
              <div className="h-80">
                <Line data={revenueChartData} options={chartOptions} />
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 p-6 rounded-xl shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center">
                    <div className="p-2 rounded-lg bg-yellow-500/20 mr-3">
                  <FiTruck className="w-5 h-5 text-yellow-400" />
                    </div>
                    Répartition des statuts
                  </h3>
                  <p className="text-sm text-gray-400 mt-1 ml-12">Distribution des déménagements</p>
                </div>
              </div>
              <div className="h-80">
                <Pie data={statusChartData} options={pieChartOptions} />
              </div>
            </div>
          </div>

          {/* Utilisateurs connectés en temps réel */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-blue-600/30 rounded-xl shadow-xl overflow-hidden mb-6">
            <div className="px-6 py-5 border-b border-blue-600/30 bg-gray-900/50 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center">
                  <div className="p-2 rounded-lg bg-blue-500/20 mr-3">
                    <FiUsers className="w-5 h-5 text-blue-400" />
                  </div>
                  Utilisateurs connectés
                </h3>
                <p className="text-sm text-gray-400 mt-1 ml-12">
                  {connectedUsers.stats.online} en ligne sur {connectedUsers.stats.total} utilisateurs
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-500/20 border border-green-500/30 rounded-lg">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-green-400 font-medium">
                    {connectedUsers.stats.online} En ligne
                  </span>
                </div>
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-gray-500/20 border border-gray-500/30 rounded-lg">
                  <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                  <span className="text-sm text-gray-400 font-medium">
                    {connectedUsers.stats.offline} Hors ligne
                  </span>
                </div>
              </div>
            </div>
            <div className="p-6">
              {loadingUsers ? (
                <div className="text-center py-8">
                  <div className="inline-block w-8 h-8 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin"></div>
                  <p className="text-gray-400 mt-4">Chargement des utilisateurs...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Statistiques par rôle */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-600/10 to-blue-800/10 border border-blue-600/30 rounded-xl p-5 hover:border-blue-500/50 transition-all shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-400 mb-1">Clients</p>
                          <p className="text-3xl font-bold text-white">
                            {connectedUsers.stats.byRole.client.online}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            sur {connectedUsers.stats.byRole.client.total} total
                          </p>
                        </div>
                        <div className="p-4 bg-blue-500/20 rounded-xl border border-blue-500/30">
                          <FiUsers className="w-7 h-7 text-blue-400" />
                        </div>
                      </div>
                      <div className="mt-4 w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all"
                          style={{ width: `${(connectedUsers.stats.byRole.client.online / Math.max(connectedUsers.stats.byRole.client.total, 1)) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-600/10 to-orange-600/10 border border-yellow-600/30 rounded-xl p-5 hover:border-yellow-500/50 transition-all shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-400 mb-1">Déménageurs</p>
                          <p className="text-3xl font-bold text-white">
                            {connectedUsers.stats.byRole.demenageur.online}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            sur {connectedUsers.stats.byRole.demenageur.total} total
                          </p>
                        </div>
                        <div className="p-4 bg-yellow-500/20 rounded-xl border border-yellow-500/30">
                          <FiTruck className="w-7 h-7 text-yellow-400" />
                        </div>
                      </div>
                      <div className="mt-4 w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-yellow-500 h-2 rounded-full transition-all"
                          style={{ width: `${(connectedUsers.stats.byRole.demenageur.online / Math.max(connectedUsers.stats.byRole.demenageur.total, 1)) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {/* Liste des utilisateurs */}
                  <div className="max-h-96 overflow-y-auto">
                    <div className="space-y-2">
                      {connectedUsers.users.length === 0 ? (
                        <div className="text-center py-12">
                          <FiUsers className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                          <p className="text-gray-400">Aucun utilisateur trouvé</p>
                        </div>
                      ) : (
                        connectedUsers.users.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-900/50 to-gray-800/30 border border-gray-700/50 rounded-xl hover:bg-gray-900/70 hover:border-gray-600 transition-all duration-200 shadow-md hover:shadow-lg"
                          >
                            <div className="flex items-center space-x-4">
                              <div className="relative">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg ${
                                  user.isOnline 
                                    ? 'bg-gradient-to-br from-green-500 to-emerald-600 border-2 border-green-400' 
                                    : 'bg-gradient-to-br from-gray-600 to-gray-700 border-2 border-gray-500'
                                }`}>
                                  {(user.firstName?.[0] || user.email?.[0] || 'U').toUpperCase()}
                                </div>
                                {user.isOnline && (
                                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-gray-900 animate-pulse shadow-lg"></div>
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-bold text-white">
                                  {user.firstName} {user.lastName}
                                </p>
                                <p className="text-xs text-gray-400">{user.email}</p>
                                <div className="flex items-center space-x-2 mt-1.5">
                                  <span className={`px-2.5 py-1 text-xs rounded-full font-medium border ${
                                    user.role === 'client' 
                                      ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' 
                                      : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                                  }`}>
                                    {user.role === 'client' ? 'Client' : 'Déménageur'}
                                  </span>
                                </div>
                                {(user.role === 'demenageur' || user.role === 'mover') && (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    <span
                                      className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-lg bg-emerald-900/50 text-emerald-200 border border-emerald-600/40"
                                      title="Crédits missions (solde)"
                                    >
                                      Solde : {user.missionCreditsBalance ?? 0}
                                    </span>
                                    <span
                                      className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-lg bg-slate-800 text-slate-200 border border-slate-600/50"
                                      title="Missions au statut terminé (completed)"
                                    >
                                      Missions terminées : {user.missionsCount ?? 0}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              <div className="text-right">
                                <p className={`text-xs font-semibold ${
                                  user.isOnline ? 'text-green-400' : 'text-gray-400'
                                }`}>
                                  {user.isOnline ? (
                                    <span className="inline-flex items-center px-3 py-1.5 bg-green-500/20 border border-green-500/30 rounded-full">
                                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                                      En ligne
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-3 py-1.5 bg-gray-500/20 border border-gray-500/30 rounded-full">
                                      <span className="w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
                                      Hors ligne
                                    </span>
                                  )}
                                </p>
                                {!user.isOnline && user.lastSeen && (
                                  <p className="text-xs text-gray-500 mt-2">
                                    Vu il y a {Math.floor((new Date() - new Date(user.lastSeen)) / 60000)} min
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Statistiques complètes - Tickets, Quotes, Conversions */}
          {completeStats && (
            <>
              {/* Tickets et Quotes */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Statistiques des tickets */}
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-blue-600/30 rounded-xl shadow-xl overflow-hidden">
                  <div className="px-6 py-5 border-b border-blue-600/30 bg-gray-900/50 flex justify-between items-center">
                    <div>
                      <h3 className="text-xl font-bold text-white flex items-center">
                        <div className="p-2 rounded-lg bg-blue-500/20 mr-3">
                          <FiHelpCircle className="w-5 h-5 text-blue-400" />
                        </div>
                        Tickets de support
                      </h3>
                      <p className="text-sm text-gray-400 mt-1 ml-12">Gestion des demandes</p>
                    </div>
                    <Link 
                      to="/admin/tickets"
                      className="text-blue-400 hover:text-blue-300 text-sm font-semibold transition-colors"
                    >
                      Voir tout →
                    </Link>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-gray-900/70 to-gray-800/50 rounded-xl p-5 border border-gray-700/50 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-gray-400 mb-2 font-medium">Total</p>
                        <p className="text-3xl font-bold text-white">{completeStats.tickets.total}</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 rounded-xl p-5 border border-blue-500/30 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-blue-400 mb-2 font-medium">Nouveaux</p>
                        <p className="text-3xl font-bold text-blue-400">{completeStats.tickets.new}</p>
                      </div>
                      <div className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 rounded-xl p-5 border border-yellow-500/30 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-yellow-400 mb-2 font-medium">Ouverts</p>
                        <p className="text-3xl font-bold text-yellow-400">{completeStats.tickets.open}</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-xl p-5 border border-green-500/30 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-green-400 mb-2 font-medium">Résolus</p>
                        <p className="text-3xl font-bold text-green-400">{completeStats.tickets.resolved}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-700/50">
                      <p className="text-xs text-gray-400 mb-3 font-medium">Par type</p>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20 text-center">
                          <p className="text-xs text-blue-400 mb-1">Qualité</p>
                          <p className="text-lg font-bold text-blue-400">{completeStats.tickets.byType.quality}</p>
                        </div>
                        <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/20 text-center">
                          <p className="text-xs text-purple-400 mb-1">Technique</p>
                          <p className="text-lg font-bold text-purple-400">{completeStats.tickets.byType.technical}</p>
                        </div>
                        <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20 text-center">
                          <p className="text-xs text-green-400 mb-1">Paiement</p>
                          <p className="text-lg font-bold text-green-400">{completeStats.tickets.byType.payment}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Statistiques des quotes/devis */}
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl shadow-xl overflow-hidden">
                  <div className="px-6 py-5 border-b border-yellow-600/30 bg-gray-900/50 flex justify-between items-center">
                    <div>
                      <h3 className="text-xl font-bold text-white flex items-center">
                        <div className="p-2 rounded-lg bg-yellow-500/20 mr-3">
                          <FiPackage className="w-5 h-5 text-yellow-400" />
                        </div>
                        Devis & Quotes
                      </h3>
                      <p className="text-sm text-gray-400 mt-1 ml-12">Gestion des devis</p>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-gray-900/70 to-gray-800/50 rounded-xl p-5 border border-gray-700/50 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-gray-400 mb-2 font-medium">Total</p>
                        <p className="text-3xl font-bold text-white">{completeStats.quotes.total}</p>
                      </div>
                      <div className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 rounded-xl p-5 border border-yellow-500/30 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-yellow-400 mb-2 font-medium">En attente</p>
                        <p className="text-3xl font-bold text-yellow-400">{completeStats.quotes.pending}</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-xl p-5 border border-green-500/30 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-green-400 mb-2 font-medium">Acceptés</p>
                        <p className="text-3xl font-bold text-green-400">{completeStats.quotes.accepted}</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-600/20 to-indigo-600/20 rounded-xl p-5 border border-blue-500/30 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-blue-400 mb-2 font-medium">Terminées</p>
                        <p className="text-3xl font-bold text-blue-400">{completeStats.quotes.completed ?? completeStats.quotes.confirmed ?? 0}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-700/50">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-900/30 rounded-lg p-3 border border-gray-700/30">
                          <p className="text-xs text-gray-400 mb-1">Prix moyen</p>
                          <p className="text-lg font-bold text-white">{formatCurrency(completeStats.quotes.avgPrice)}</p>
                        </div>
                        <div className="bg-yellow-500/10 rounded-lg p-3 border border-yellow-500/20">
                          <p className="text-xs text-yellow-400 mb-1">Derniers 7 jours</p>
                          <p className="text-lg font-bold text-yellow-400">{completeStats.quotes.last7Days}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top déménageurs et métriques de conversion */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top déménageurs */}
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl shadow-xl overflow-hidden">
                  <div className="px-6 py-5 border-b border-yellow-600/30 bg-gray-900/50">
                    <h3 className="text-xl font-bold text-white flex items-center">
                      <div className="p-2 rounded-lg bg-yellow-500/20 mr-3">
                        <FiAward className="w-5 h-5 text-yellow-400" />
                      </div>
                      Top déménageurs
                    </h3>
                    <p className="text-sm text-gray-400 mt-1 ml-12">Les meilleurs performeurs</p>
                  </div>
                  <div className="p-6">
                    {completeStats.topMovers.length === 0 ? (
                      <p className="text-center text-gray-400 py-8">Aucun déménageur pour le moment</p>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {completeStats.topMovers.slice(0, 5).map((mover, index) => (
                          <div key={mover.id} className="bg-gradient-to-br from-gray-900/70 to-gray-800/50 rounded-xl p-5 border border-gray-700/50 shadow-lg hover:shadow-xl hover:border-yellow-500/30 transition-all">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-4">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shadow-lg ${
                                  index === 0 ? 'bg-gradient-to-br from-yellow-500 to-orange-500 text-white border-2 border-yellow-400' :
                                  index === 1 ? 'bg-gradient-to-br from-gray-400 to-gray-500 text-white border-2 border-gray-300' :
                                  index === 2 ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white border-2 border-orange-400' :
                                  'bg-gradient-to-br from-gray-600 to-gray-700 text-white border-2 border-gray-500'
                                }`}>
                                  {index + 1}
                                </div>
                                <div>
                                  <p className="text-sm font-bold text-white">
                                    {mover.firstName} {mover.lastName}
                                  </p>
                                  <p className="text-xs text-gray-400">{mover.companyName || mover.email}</p>
                                </div>
                              </div>
                              {mover.rating && (
                                <div className="flex items-center px-3 py-1.5 bg-yellow-500/20 border border-yellow-500/30 rounded-full">
                                  <FiAward className="text-yellow-400 mr-1 w-4 h-4" />
                                  <span className="text-xs text-yellow-400 font-bold">
                                    {mover.rating.toFixed(1)}/5
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-700/50">
                              <div className="text-center bg-gray-800/50 rounded-lg p-2">
                                <p className="text-xs text-gray-400 mb-1">Devis</p>
                                <p className="text-lg font-bold text-white">{mover.totalQuotes}</p>
                              </div>
                              <div className="text-center bg-blue-500/10 rounded-lg p-2 border border-blue-500/20">
                                <p className="text-xs text-blue-400 mb-1">Terminées</p>
                                <p className="text-lg font-bold text-blue-400">{mover.completedQuotes ?? 0}</p>
                              </div>
                              <div className="text-center bg-green-500/10 rounded-lg p-2 border border-green-500/20">
                                <p className="text-xs text-green-400 mb-1">Revenus</p>
                                <p className="text-sm font-bold text-green-400">{formatCurrency(mover.totalRevenue)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Métriques de conversion */}
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-green-600/30 rounded-xl shadow-xl overflow-hidden">
                  <div className="px-6 py-5 border-b border-green-600/30 bg-gray-900/50">
                    <h3 className="text-xl font-bold text-white flex items-center">
                      <div className="p-2 rounded-lg bg-green-500/20 mr-3">
                        <FiTarget className="w-5 h-5 text-green-400" />
                      </div>
                      Métriques de conversion
                    </h3>
                    <p className="text-sm text-gray-400 mt-1 ml-12">Performance des conversions</p>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      <div className="bg-gradient-to-br from-green-600/10 to-emerald-600/10 rounded-xl p-5 border border-green-500/30 shadow-lg">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-sm text-gray-300 font-medium">Taux d'acceptation</span>
                          <span className="text-2xl font-bold text-green-400">{completeStats.conversion.acceptanceRate}%</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-3 mb-2">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full transition-all shadow-lg"
                            style={{ width: `${completeStats.conversion.acceptanceRate}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-400">
                          {completeStats.conversion.acceptedQuotes} acceptés / {completeStats.conversion.totalQuotes} devis
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-600/10 to-indigo-600/10 rounded-xl p-5 border border-blue-500/30 shadow-lg">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-sm text-gray-300 font-medium">Taux des missions terminées</span>
                          <span className="text-2xl font-bold text-blue-400">{completeStats.conversion.missionsCompletionRate}%</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-3 mb-2">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-indigo-500 h-3 rounded-full transition-all shadow-lg"
                            style={{ width: `${Math.min(100, completeStats.conversion.missionsCompletionRate || 0)}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-400">
                          {completeStats.conversion.completedQuotes} terminées / {completeStats.conversion.totalQuotes} devis
                        </p>
                      </div>
                      <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-700/50">
                        <div className="text-center bg-gray-900/30 rounded-lg p-4 border border-gray-700/30">
                          <p className="text-3xl font-bold text-white">{completeStats.conversion.totalQuotes}</p>
                          <p className="text-xs text-gray-400 mt-1">Total devis</p>
                        </div>
                        <div className="text-center bg-green-500/10 rounded-lg p-4 border border-green-500/30">
                          <p className="text-3xl font-bold text-green-400">{completeStats.conversion.acceptedQuotes}</p>
                          <p className="text-xs text-gray-400 mt-1">Acceptés</p>
                        </div>
                        <div className="text-center bg-red-500/10 rounded-lg p-4 border border-red-500/30">
                          <p className="text-3xl font-bold text-red-400">{completeStats.conversion.rejectedQuotes}</p>
                          <p className="text-xs text-gray-400 mt-1">Annulés</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages et Paiements */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Statistiques des messages */}
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-purple-600/30 rounded-xl shadow-xl overflow-hidden">
                  <div className="px-6 py-5 border-b border-purple-600/30 bg-gray-900/50 flex justify-between items-center">
                    <div>
                      <h3 className="text-xl font-bold text-white flex items-center">
                        <div className="p-2 rounded-lg bg-purple-500/20 mr-3">
                          <FiMessageSquare className="w-5 h-5 text-purple-400" />
                        </div>
                        Messages
                      </h3>
                      <p className="text-sm text-gray-400 mt-1 ml-12">Communication sur la plateforme</p>
                    </div>
                    <Link 
                      to="/admin/messages"
                      className="text-purple-400 hover:text-purple-300 text-sm font-semibold transition-colors"
                    >
                      Voir tout →
                    </Link>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-gray-900/70 to-gray-800/50 rounded-xl p-5 border border-gray-700/50 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-gray-400 mb-2 font-medium">Total messages</p>
                        <p className="text-3xl font-bold text-white">{formatNumber(completeStats.messages.total)}</p>
                      </div>
                      <div className="bg-gradient-to-br from-red-600/20 to-pink-600/20 rounded-xl p-5 border border-red-500/30 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-red-400 mb-2 font-medium">Non lus</p>
                        <p className="text-3xl font-bold text-red-400">{completeStats.messages.unread}</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-600/20 to-indigo-600/20 rounded-xl p-5 border border-blue-500/30 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-blue-400 mb-2 font-medium">Dernières 24h</p>
                        <p className="text-3xl font-bold text-blue-400">{completeStats.messages.last24h}</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-xl p-5 border border-green-500/30 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-green-400 mb-2 font-medium">Expéditeurs actifs</p>
                        <p className="text-3xl font-bold text-green-400">{completeStats.messages.activeSenders}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Statistiques des paiements */}
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-green-600/30 rounded-xl shadow-xl overflow-hidden">
                  <div className="px-6 py-5 border-b border-green-600/30 bg-gray-900/50 flex justify-between items-center">
                    <div>
                      <h3 className="text-xl font-bold text-white flex items-center">
                        <div className="p-2 rounded-lg bg-green-500/20 mr-3">
                          <FiDollarSign className="w-5 h-5 text-green-400" />
                        </div>
                        Paiements
                      </h3>
                      <p className="text-sm text-gray-400 mt-1 ml-12">Transactions financières</p>
                    </div>
                    <Link 
                      to="/admin/payments"
                      className="text-green-400 hover:text-green-300 text-sm font-semibold transition-colors"
                    >
                      Voir tout →
                    </Link>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-gray-900/70 to-gray-800/50 rounded-xl p-5 border border-gray-700/50 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-gray-400 mb-2 font-medium">Total</p>
                        <p className="text-3xl font-bold text-white">{completeStats.payments.total}</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-xl p-5 border border-green-500/30 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-green-400 mb-2 font-medium">Réussis</p>
                        <p className="text-3xl font-bold text-green-400">{completeStats.payments.successful}</p>
                      </div>
                      <div className="bg-gradient-to-br from-red-600/20 to-pink-600/20 rounded-xl p-5 border border-red-500/30 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-red-400 mb-2 font-medium">Échoués</p>
                        <p className="text-3xl font-bold text-red-400">{completeStats.payments.failed}</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-600/20 to-indigo-600/20 rounded-xl p-5 border border-blue-500/30 shadow-lg hover:shadow-xl transition-all">
                        <p className="text-xs text-blue-400 mb-2 font-medium">Total payé</p>
                        <p className="text-2xl font-bold text-blue-400">{formatCurrency(completeStats.payments.totalPaid)}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-700/50">
                      <div className="bg-gradient-to-r from-blue-600/10 to-indigo-600/10 rounded-lg p-4 border border-blue-500/20">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-300 font-medium">Paiement moyen</span>
                          <span className="text-xl font-bold text-blue-400">{formatCurrency(completeStats.payments.avgPayment)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activité récente */}
              {completeStats.recentActivity && completeStats.recentActivity.length > 0 && (
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-blue-600/30 rounded-xl shadow-xl overflow-hidden">
                  <div className="px-6 py-5 border-b border-blue-600/30 bg-gray-900/50">
                    <h3 className="text-xl font-bold text-white flex items-center">
                      <div className="p-2 rounded-lg bg-blue-500/20 mr-3">
                        <FiActivity className="w-5 h-5 text-blue-400" />
                      </div>
                      Activité récente
                    </h3>
                    <p className="text-sm text-gray-400 mt-1 ml-12">Dernières actions sur la plateforme</p>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {completeStats.recentActivity.map((activity, index) => (
                        <div key={index} className="flex items-start space-x-4 p-4 bg-gradient-to-r from-gray-900/70 to-gray-800/50 rounded-xl border border-gray-700/50 hover:border-gray-600 transition-all shadow-md hover:shadow-lg">
                          <div className={`w-3 h-3 rounded-full mt-2 flex-shrink-0 ${
                            activity.type === 'user_registered' ? 'bg-green-500 shadow-lg shadow-green-500/50' :
                            activity.type === 'quote_created' ? 'bg-blue-500 shadow-lg shadow-blue-500/50' :
                            'bg-yellow-500 shadow-lg shadow-yellow-500/50'
                          }`}></div>
                          <div className="flex-1">
                            <p className="text-sm text-white font-medium">
                              <span className="font-bold">
                                {activity.type === 'user_registered' ? 'Nouvel utilisateur' :
                                 activity.type === 'quote_created' ? 'Nouveau devis' :
                                 'Nouveau ticket'}
                              </span>
                              {activity.type === 'user_registered' && (
                                <span className="text-gray-400 ml-2">({activity.metadata})</span>
                              )}
                            </p>
                            <p className="text-xs text-gray-400 mt-1.5">{activity.entityName}</p>
                            <p className="text-xs text-gray-500 mt-2 flex items-center">
                              <FiClock className="w-3 h-3 mr-1" />
                              {new Date(activity.date).toLocaleString('fr-FR', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Derniers déménagements */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl shadow-xl overflow-hidden">
            <div className="px-6 py-5 border-b border-yellow-600/30 bg-gray-900/50 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center">
                  <div className="p-2 rounded-lg bg-yellow-500/20 mr-3">
                    <FiTruck className="w-5 h-5 text-yellow-400" />
                  </div>
                  Derniers déménagements
                </h3>
                <p className="text-sm text-gray-400 mt-1 ml-12">Activité récente de la plateforme</p>
              </div>
              <Link 
                to="/admin/moves"
                className="flex items-center px-4 py-2 text-sm font-semibold text-white bg-yellow-600 hover:bg-yellow-500 rounded-lg transition-all shadow-lg hover:shadow-xl"
              >
                Voir tout <FiChevronRight className="ml-1" />
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                      De
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                      À
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {stats.recentMoves.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                        Aucun déménagement récent
                      </td>
                    </tr>
                  ) : (
                    stats.recentMoves.map((move, index) => (
                      <tr key={index} className="hover:bg-gray-700/30 transition-all duration-200 border-b border-gray-700/30">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center text-white font-bold mr-3 shadow-lg">
                              {(move.client_name?.[0] || 'N')[0].toUpperCase()}
                            </div>
                            <div className="text-sm font-bold text-white">
                              {move.client_name || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-300 max-w-xs truncate">
                          <div className="flex items-center">
                            <FiTruck className="w-4 h-4 mr-2 text-gray-500" />
                            {move.from_address || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-300 max-w-xs truncate">
                          <div className="flex items-center">
                            <FiTruck className="w-4 h-4 mr-2 text-gray-500" />
                            {move.to_address || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          <div className="flex items-center">
                            <FiCalendar className="w-4 h-4 mr-2 text-gray-500" />
                            {formatDate(move.move_date)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1.5 inline-flex items-center text-xs leading-5 font-bold rounded-full border ${getStatusColor(move.status)}`}>
                            {move.status === 'accepted' || move.status === 'accepté' ? 'Accepté' :
                             move.status === 'confirmé' || move.status === 'confirmed' ? 'Terminé' :
                             move.status === 'en_attente' ? 'En attente' :
                             move.status === 'annulé' ? 'Annulé' :
                             move.status === 'terminé' ? 'Terminé' : move.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                          <Link 
                            to={`/admin/moves/${move.id}`}
                            className="inline-flex items-center px-3 py-1.5 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 rounded-lg border border-yellow-600/30 transition-all hover:border-yellow-500/50"
                          >
                            Voir
                            <FiArrowUpRight className="ml-1 w-3 h-3" />
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminHome;
